import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { vibrate } from "@/lib/api";
import { initPixels, trackEvent } from "@/lib/analytics";
import { BrandMark } from "@/components/landing/primitives";
import Hero from "@/components/landing/Hero";
import { ProblemSection, HowItWorks } from "@/components/landing/Sections";
import { PricingPreview, FAQSection, FinalCTA, LandingFooter, FAQ_ITEMS, TestimonialsSection } from "@/components/landing/Closing";
import { DemoShowcase } from "@/components/landing/DemoShowcase";
import { SocialProofStrip, QuizHook, FeaturesBento, VerdictDeepDive, CreatorBand } from "@/components/landing/Engage";
import CookieConsent from "@/components/CookieConsent";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const SITE = "https://doclove.app";

function jsonLd() {
  const data = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "DocLove",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Web, iOS, Android (PWA)",
      description: "L'IA qui décode tes conversations, détecte les red flags et suit tes relations dans le temps.",
      url: SITE,
      offers: [
        { "@type": "Offer", price: "0", priceCurrency: "EUR", name: "Free" },
        { "@type": "Offer", price: "4.90", priceCurrency: "EUR", name: "Basic (lancement)" },
        { "@type": "Offer", price: "14.99", priceCurrency: "EUR", name: "Pro" },
        { "@type": "Offer", price: "29.99", priceCurrency: "EUR", name: "Docteur" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];
  return JSON.stringify(data);
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("dmd_ref", ref.toUpperCase());
    const vip = params.get("vip");
    if (vip) localStorage.setItem("dmd_vip", vip.toUpperCase());
    const promo = params.get("promo");
    if (promo) localStorage.setItem("dmd_promo", promo.toUpperCase());
    const creator = params.get("c");
    if (creator) localStorage.setItem("dmd_creator", creator.toUpperCase());
    initPixels();
    trackEvent("page_view");
  }, []);

  const goStart = () => {
    vibrate();
    trackEvent("lead", { location: "nav" });
    navigate(user ? "/app" : "/onboarding");
  };

  return (
    <div className="grain min-h-screen bg-[#09090E] text-slate-100 relative overflow-x-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd() }} />

      {/* Top announcement bar — Creator program (always visible) */}
      <Link
        to="/createurs"
        data-testid="creator-announcement-bar"
        className="group fixed top-0 inset-x-0 z-50 h-9 flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-500 text-white text-xs sm:text-sm font-bold px-4 text-center"
      >
        <span className="truncate">🤝 Créateurs & influenceurs : gagne <span className="underline underline-offset-2">50%</span> en parlant de DocLove</span>
        <span className="inline-flex items-center gap-1 whitespace-nowrap group-hover:translate-x-0.5 transition-transform">
          Rejoindre <ArrowRight size={14} />
        </span>
      </Link>

      <header className="glass fixed top-9 inset-x-0 z-40 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark size={22} />
            <span className="font-display text-lg font-bold tracking-tight">DocLove</span>
          </div>
          <button
            data-testid="nav-cta"
            onClick={goStart}
            className="rounded-full bg-gradient-to-r from-rose-600 to-purple-600 text-white text-sm font-semibold py-2 px-5 hover:shadow-[0_0_28px_rgba(225,29,72,0.5)] transition-shadow shadow-[0_0_18px_rgba(225,29,72,0.35)]"
          >
            {user ? "Ouvrir l'app" : "Analyser ma conv"}
          </button>
        </div>
      </header>

      <main>
        <Hero />
        <SocialProofStrip />
        <QuizHook />
        <ProblemSection />
        <FeaturesBento />
        <VerdictDeepDive />
        <HowItWorks />
        <DemoShowcase />
        <TestimonialsSection />
        <CreatorBand />
        <PricingPreview />
        <FAQSection />
        <FinalCTA />
      </main>
      <LandingFooter />
      <CookieConsent />
    </div>
  );
}
